// src/hooks/useProjects.js
import {
  createProject,
  createProjectType,
  getProjectById,
  getProjects,
  getProjectTypeById,
  getProjectTypeCustomFields,
  getProjectTypes,
  updateProject,
  updateProjectType,
} from '@/api/projects';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  APIErrorResponse,
  ICreateProjectForm,
  ICreateProjectTypeForm,
} from '@/types';
import { useAuth } from '@/providers/AuthProvider';
import { handleAPIError } from '@/utils/errorHandler';
import { ProjectDetail } from '@packages/database/types';
import { AxiosError } from 'axios';

export const useProjects = () => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  const query = useQuery({
    queryKey: ['projects', orgId],
    queryFn: getProjects,
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    projects: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export const useUniqueProject = (projectId: string) => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  const query = useQuery({
    queryKey: ['projects', orgId, projectId],
    queryFn: () => getProjectById(projectId!),
    enabled: !!projectId && !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation<
    ProjectDetail,
    AxiosError<APIErrorResponse>,
    any
  >({
    mutationFn: createProject,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['projects', orgId],
      });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    createProject: mutation.mutate,
    createProjectAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation({
    mutationFn: updateProject,
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ['projects', orgId],
      });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    updateProject: mutation.mutate,
    updateProjectAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};

// Project tYpe queries

export const useUniqueProjectType = (projectTypeId: string) => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  const query = useQuery({
    queryKey: ['projectTypes', orgId, projectTypeId],
    queryFn: () => getProjectTypeById(projectTypeId!),
    enabled: !!projectTypeId && !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    projectType: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export const useProjectTypeCustomFields = (projectTypeId: string) => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  const query = useQuery({
    queryKey: ['projectTypes', orgId, projectTypeId, 'custom-fields'],
    queryFn: () => getProjectTypeCustomFields(projectTypeId!),
    enabled: !!projectTypeId && !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export const useProjectTypes = () => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  const query = useQuery({
    queryKey: ['projectTypes', orgId],
    queryFn: getProjectTypes,
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    projectTypes: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export const useCreateProjectType = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation({
    mutationFn: (projectTypeData: any) => createProjectType(projectTypeData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['projectTypes', orgId],
      });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    createProjectType: mutation.mutate,
    createProjectTypeAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useUpdateProjectType = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation({
    mutationFn: updateProjectType,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['projectTypes', orgId],
      });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    updateProjectType: mutation.mutate,
    updateProjectTypeAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};
